import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { authService } from "../services/authService";
import { AuthState, LoginCredentials, User, Tokens } from "../types";
import { logger } from "../utils/logger";

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshTokens: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: "LOGIN_START" }
  | { type: "LOGIN_SUCCESS"; payload: { user: User; tokens: Tokens } }
  | { type: "LOGIN_FAILURE" }
  | { type: "LOGOUT" }
  | { type: "UPDATE_USER"; payload: User }
  | { type: "UPDATE_TOKENS"; payload: Tokens }
  | { type: "SET_LOADING"; payload: boolean };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN_START":
      return { ...state, isLoading: true };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
      };
    case "LOGIN_FAILURE":
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case "LOGOUT":
      return {
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case "UPDATE_USER":
      return { ...state, user: action.payload };
    case "UPDATE_TOKENS":
      return { ...state, tokens: action.payload };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Function to refresh tokens when they're about to expire
  const refreshTokens = useCallback(async () => {
    try {
      const storedTokens = authService.getStoredTokens();
      if (!storedTokens?.RefreshToken) {
        throw new Error("No refresh token available");
      }

      const storedUser = localStorage.getItem("adminUser");
      const user = storedUser ? JSON.parse(storedUser) : null;
      const username = user?.username;

      const result = await authService.refreshToken(storedTokens.RefreshToken, username);
      
      // Update the tokens in state
      const updatedTokens = {
        ...storedTokens,
        AccessToken: result.accessToken,
        ...(result.idToken && { IdToken: result.idToken }),
      };
      
      // Update ExpiresAt if ExpiresIn is provided
      if (result.expiresIn) {
        const expiresAt = Date.now() + result.expiresIn * 1000;
        localStorage.setItem("adminExpiresAt", expiresAt.toString());
      }
      
      dispatch({ type: "UPDATE_TOKENS", payload: updatedTokens });
      logger.debug("Tokens refreshed successfully");
    } catch (error) {
      logger.error("Token refresh failed");
      // If refresh fails, logout the user
      authService.logout();
      dispatch({ type: "LOGOUT" });
      throw error;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // First check for stored tokens
        const storedTokens = authService.getStoredTokens();
        const storedAccessToken = localStorage.getItem("adminToken");
        
        if (storedTokens?.AccessToken || storedAccessToken) {
          const accessToken = storedTokens?.AccessToken || storedAccessToken;
          
          if (!accessToken) {
            throw new Error("No access token found");
          }

          // Check if token is expired
          if (authService.isTokenExpired(accessToken)) {
            logger.debug("Access token expired, attempting refresh...");
            
            if (storedTokens?.RefreshToken) {
              try {
                await refreshTokens();
                // After refresh, get the updated tokens and validate
                const newTokens = authService.getStoredTokens();
                if (newTokens?.AccessToken) {
                  const user = await authService.verifyToken(newTokens.AccessToken);
                  dispatch({ type: "LOGIN_SUCCESS", payload: { user, tokens: newTokens } });
                  return;
                }
              } catch (refreshError) {
                logger.error("Token refresh failed during init");
                throw refreshError;
              }
            } else {
              throw new Error("No refresh token available");
            }
          } else {
            // Token is still valid, verify it
            const user = await authService.verifyToken(accessToken);
            dispatch({ type: "LOGIN_SUCCESS", payload: { 
              user, 
              tokens: storedTokens || { 
                AccessToken: accessToken,
                IdToken: "",
                RefreshToken: "",
                ExpiresIn: 0,
                TokenType: "Bearer"
              }
            }});
            return;
          }
        }
        
        throw new Error("No valid tokens found");
      } catch (error) {
        logger.debug("Auth initialization failed:", error);
        // Clear any invalid tokens
        authService.logout();
        dispatch({ type: "LOGIN_FAILURE" });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    initAuth();
  }, [refreshTokens]);

  // Set up automatic token refresh
  useEffect(() => {
    if (!state.tokens?.AccessToken) return;

    const checkTokenExpiration = () => {
      if (state.tokens?.AccessToken && authService.isTokenExpired(state.tokens.AccessToken)) {
        logger.debug("Token expired, refreshing...");
        refreshTokens().catch(() => {
          logger.error("Auto token refresh failed");
        });
      }
    };

    // Check token expiration every 5 minutes
    const interval = setInterval(checkTokenExpiration, 5 * 60 * 1000);

    // Also check when the component mounts
    checkTokenExpiration();

    return () => clearInterval(interval);
  }, [state.tokens, refreshTokens]);

  const login = async (credentials: LoginCredentials) => {
    dispatch({ type: "LOGIN_START" });
    try {
      const response = await authService.login(credentials);
      dispatch({ type: "LOGIN_SUCCESS", payload: response });
    } catch (error) {
      dispatch({ type: "LOGIN_FAILURE" });
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    dispatch({ type: "LOGOUT" });
  };

  const updateUser = (user: User) => {
    dispatch({ type: "UPDATE_USER", payload: user });
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    updateUser,
    refreshTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};